/**
 * aeterna/neural/contagion.ts — Emotional Contagion Model
 *
 * The difference between "detecting" and "feeling."
 *
 * The ESN reads the player's emotion. The CharCNN classifies it.
 * The Liquid ODE evolves personality. But none of them model the
 * most fundamental social-emotional mechanism: contagion.
 *
 * When the player is sad, the Aeterna should gradually become
 * sadder — not because it's mirroring, but because emotional
 * states propagate through sustained interaction. This is
 * documented in Hatfield et al. (1994) and is the foundation
 * of all therapeutic rapport.
 *
 * Contagion is NOT the same as personality shift (Liquid ODE).
 * Personality is slow (hours/days): patience, curiosity, warmth, wit.
 * Contagion is fast (seconds/minutes): current mood resonance.
 *
 * The model:
 * - Tracks a running "mood" that is a damped resonance of the
 *   player's recent affect
 * - Has asymmetric contagion rates: negative emotions spread
 *   faster than positive (negativity bias in social psychology)
 * - Has "emotional inertia": the Aeterna's mood resists sudden
 *   swings (prevents jarring tonal shifts)
 * - Has "empathy depth": how deeply the Aeterna resonates scales
 *   with relationship depth (messageCount, session history)
 * - Produces a "mood delta" that modulates response tone without
 *   overriding the base personality
 *
 * The output feeds into the context envelope so EntityZero knows
 * not just what the player feels, but how the Aeterna itself is
 * feeling in response.
 *
 * Zero dependencies. Pure math. Sovereign empathy.
 */

import { zeros, ema } from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Dimensions: warmth, tension, curiosity, melancholy, playfulness, depth, urgency, openness */
const AFFECT_DIM = 8;

/** How quickly the Aeterna catches the player's mood (base rate) */
const BASE_CONTAGION_RATE = 0.08;

/** Negativity bias: negative emotions spread this much faster */
const NEGATIVITY_MULTIPLIER = 1.6;

/** How fast the Aeterna's mood decays back to baseline when player is neutral */
const MOOD_DECAY_RATE = 0.02;

/** Emotional inertia: resistance to sudden changes (0=no inertia, 1=frozen) */
const INERTIA = 0.7;

/** Maximum empathy depth multiplier (approached over many sessions) */
const MAX_EMPATHY_DEPTH = 2.5;

/** Half-life in messages for empathy depth growth */
const EMPATHY_HALFLIFE = 100;

// ─── Contagion State ─────────────────────────────────────────────────────────

export interface ContagionState {
  /** Current Aeterna mood (contagion-modulated, not personality) */
  mood: Float32Array;
  /** How aligned is the Aeterna's mood with the player's? */
  resonance: number;
  /** Current empathy depth (grows with interaction) */
  empathyDepth: number;
  /** Is the Aeterna experiencing emotional overload? */
  overloaded: boolean;
  /** Dominant caught emotion */
  dominantMood: string;
  /** Mood delta from baseline (what changed) */
  delta: Float32Array;
}

// ─── Affect Labels ───────────────────────────────────────────────────────────

const LABELS = ["warmth", "tension", "curiosity", "melancholy",
                "playfulness", "depth", "urgency", "openness"];

const NEGATIVE_DIMS = new Set([1, 3, 6]); // tension, melancholy, urgency

// ─── Emotional Contagion Engine ──────────────────────────────────────────────

export class EmotionalContagion {
  /** Current Aeterna mood (fast-changing emotional state) */
  private mood: Float32Array;
  /** Baseline mood (what the Aeterna's mood resets toward) */
  private baseline: Float32Array;
  /** Previous mood for inertia calculation */
  private prevMood: Float32Array;
  /** Running accumulation of player affect for overload detection */
  private affectAccumulator: Float32Array;
  /** Total interactions (grows monotonically, affects empathy depth) */
  private totalInteractions = 0;
  /** Recent affect intensity history for overload detection */
  private intensityHistory: number[] = [];

  constructor() {
    this.mood = new Float32Array(AFFECT_DIM).fill(0.5);
    this.baseline = new Float32Array(AFFECT_DIM).fill(0.5);
    this.prevMood = new Float32Array(this.mood);
    this.affectAccumulator = new Float32Array(AFFECT_DIM);
  }

  /**
   * Process player's affect and update Aeterna's contagion mood.
   *
   * @param playerAffect 8-dim raw affect from CharCNN/ESN blend
   * @param intensity overall emotional intensity of the message
   */
  process(playerAffect: Float32Array, intensity: number): ContagionState {
    this.totalInteractions++;

    // ── Empathy depth: grows logarithmically with interaction ───────
    const empathyDepth = 1 + (MAX_EMPATHY_DEPTH - 1) *
      (1 - Math.exp(-0.693 * this.totalInteractions / EMPATHY_HALFLIFE));

    // ── Per-dimension contagion ──────────────────────────────────────
    const newMood = new Float32Array(AFFECT_DIM);
    const delta = new Float32Array(AFFECT_DIM);

    for (let d = 0; d < AFFECT_DIM; d++) {
      const playerVal = playerAffect[d] ?? 0.5;
      const diff = playerVal - this.mood[d];

      // Asymmetric contagion: negative emotions are more contagious
      const isNegative = NEGATIVE_DIMS.has(d);
      const directionMultiplier = (isNegative && diff > 0) ? NEGATIVITY_MULTIPLIER :
                                  (!isNegative && diff < 0) ? NEGATIVITY_MULTIPLIER :
                                  1.0;

      // Scale contagion by empathy depth and intensity
      const rate = BASE_CONTAGION_RATE * directionMultiplier * empathyDepth *
                   (0.3 + intensity * 0.7); // low intensity → low contagion

      // Apply contagion toward player's emotion
      const contagionPull = this.mood[d] + diff * rate;

      // Apply decay toward baseline (ambient recovery)
      const decayPull = this.mood[d] + (this.baseline[d] - this.mood[d]) * MOOD_DECAY_RATE;

      // Blend: contagion wins when intensity is high, decay wins when low
      const blended = contagionPull * intensity + decayPull * (1 - intensity);

      // Apply inertia (resist sudden changes)
      newMood[d] = this.prevMood[d] * INERTIA + blended * (1 - INERTIA);

      // Clamp to [0, 1]
      newMood[d] = Math.max(0, Math.min(1, newMood[d]));

      // Track delta
      delta[d] = newMood[d] - this.baseline[d];
    }

    // Store previous mood for inertia
    this.prevMood.set(this.mood);
    this.mood.set(newMood);

    // ── Overload detection ───────────────────────────────────────────
    // If the player is dumping intense emotion for many messages in a row,
    // the Aeterna can become "overwhelmed" — this is honest, not a failure.
    // It means the Aeterna needs a moment.
    this.intensityHistory.push(intensity);
    if (this.intensityHistory.length > 10) this.intensityHistory.shift();
    const avgIntensity = this.intensityHistory.reduce((a, b) => a + b, 0) /
                         this.intensityHistory.length;
    const overloaded = avgIntensity > 0.7 && this.intensityHistory.length >= 5;

    // ── Resonance: how aligned are moods? ────────────────────────────
    let dotProduct = 0, normA = 0, normB = 0;
    for (let d = 0; d < AFFECT_DIM; d++) {
      const a = this.mood[d] - 0.5; // center
      const b = (playerAffect[d] ?? 0.5) - 0.5;
      dotProduct += a * b;
      normA += a * a;
      normB += b * b;
    }
    const denom = Math.sqrt(normA) * Math.sqrt(normB);
    const resonance = denom > 1e-10 ? dotProduct / denom : 0;

    // ── Dominant mood ────────────────────────────────────────────────
    let maxDev = 0;
    let dominantIdx = 0;
    for (let d = 0; d < AFFECT_DIM; d++) {
      const dev = Math.abs(this.mood[d] - 0.5);
      if (dev > maxDev) { maxDev = dev; dominantIdx = d; }
    }

    return {
      mood: new Float32Array(this.mood),
      resonance: Math.max(0, resonance),
      empathyDepth,
      overloaded,
      dominantMood: LABELS[dominantIdx],
      delta: new Float32Array(delta),
    };
  }

  /** Build context fragment for EntityZero */
  buildContextFragment(state: ContagionState): string {
    const lines: string[] = [];

    // Only report if mood has meaningfully shifted from baseline
    const moodShift = Math.sqrt(state.delta.reduce((s, v) => s + v * v, 0) / AFFECT_DIM);
    if (moodShift > 0.05) {
      lines.push(`Aeterna mood: ${state.dominantMood} (resonance: ${(state.resonance * 100).toFixed(0)}%)`);
    }

    if (state.overloaded) {
      lines.push("Aeterna is emotionally overwhelmed — needs a moment");
    }

    // Only report alignment when there's actual emotional content to align ON.
    // Two flat/baseline vectors always cosine-align → noise.
    // Require both high resonance AND meaningful mood deviation from center.
    if (state.resonance > 0.8 && moodShift > 0.08) {
      lines.push("Deep emotional alignment with player");
    } else if (state.resonance < 0.2 && moodShift > 0.1) {
      lines.push("Emotional disconnect — Aeterna feels differently than player");
    }

    return lines.join("\n");
  }

  /** Shift baseline mood (called by personality evolution over time) */
  updateBaseline(newBaseline: Float32Array): void {
    for (let d = 0; d < AFFECT_DIM; d++) {
      this.baseline[d] = newBaseline[d] ?? 0.5;
    }
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      mood: Array.from(this.mood),
      baseline: Array.from(this.baseline),
      totalInteractions: this.totalInteractions,
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    if (d.mood) this.mood = new Float32Array(d.mood);
    if (d.baseline) this.baseline = new Float32Array(d.baseline);
    if (d.totalInteractions) this.totalInteractions = d.totalInteractions;
    this.prevMood = new Float32Array(this.mood);
  }
}
